import { useCallback, useState } from 'react';

import {
  ClockCircleOutlined,
  LoginOutlined,
  LogoutOutlined,
  PictureOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  Col,
  Image,
  Modal,
  Row,
  Skeleton,
  Spin,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useTranslation } from 'react-i18next';

import { AttendanceUserTurnDetail } from '../types';
import { CHECKIN_STATUS_COLORS, CheckinStatusKey } from '../utils';
import EvidenceMap from './EvidenceMap';

dayjs.extend(duration);

const BASE = 'scenes.kit.dashboard_alternative_mode.components.evidence_modal';

const { Text } = Typography;

interface EvidenceModalProps {
  open: boolean;
  loading: boolean;
  detail: AttendanceUserTurnDetail | undefined;
  onClose: () => void;
  touchLatamApiUrl: string;
}

function formatElapsed(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return '--';
  const d = dayjs.duration(seconds, 'seconds');

  const days = Math.floor(d.asDays());
  const hours = d.hours();
  const minutes = d.minutes();

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${String(hours).padStart(2, '0')}h`);
  parts.push(`${String(minutes).padStart(2, '0')}m`);

  return parts.join(' ');
}

function TimeBlock({
  icon,
  label,
  scheduled,
  actual,
}: {
  icon: React.ReactNode;
  label: string;
  scheduled: string;
  actual: string | null;
}) {
  return (
    <div className="kit-evidence-modal__time-block">
      <div className="kit-evidence-modal__time-block-header">
        {icon}
        <Text className="kit-evidence-modal__time-block-label">{label}</Text>
      </div>
      <div className="kit-evidence-modal__time-block-body">
        <div className="kit-evidence-modal__time-item">
          <Text className="kit-evidence-modal__time-item-meta">Pactado</Text>
          <Text className="kit-evidence-modal__time-item-value">
            {scheduled}
          </Text>
        </div>
        <div className="kit-evidence-modal__time-divider" aria-hidden="true" />
        <div className="kit-evidence-modal__time-item">
          <Text className="kit-evidence-modal__time-item-meta">Real</Text>
          <Text
            className={[
              'kit-evidence-modal__time-item-value',
              actual ? '' : 'kit-evidence-modal__time-item-value--empty',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {actual ?? '--'}
          </Text>
        </div>
      </div>
    </div>
  );
}

function PhotoCard({ label, imageUrl }: { label: string; imageUrl?: string }) {
  const { t } = useTranslation();
  const [broken, setBroken] = useState(false);
  const [loading, setLoading] = useState(!!imageUrl);

  const showPlaceholder = !imageUrl || broken;

  const placeholder = (
    <div
      className={[
        'kit-evidence-modal__photo-placeholder',
        broken ? 'kit-evidence-modal__photo-placeholder--broken' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={label}
    >
      <PictureOutlined aria-hidden="true" />
    </div>
  );

  return (
    <div className="kit-evidence-modal__photo">
      {showPlaceholder ? (
        broken ? (
          <Tooltip title={t(`${BASE}.photos.broken`)}>{placeholder}</Tooltip>
        ) : (
          placeholder
        )
      ) : (
        <div className="kit-evidence-modal__photo-image-wrapper">
          {loading && (
            <div className="kit-evidence-modal__photo-placeholder">
              <Spin size="small" />
            </div>
          )}
          <Image
            src={imageUrl}
            alt={label}
            className="kit-evidence-modal__photo-image"
            style={{ display: loading ? 'none' : undefined }}
            preview={{ mask: null }}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setBroken(true);
            }}
          />
        </div>
      )}
      <Text className="kit-evidence-modal__photo-label">{label}</Text>
    </div>
  );
}

function EvidenceModal({
  open,
  loading,
  detail,
  onClose,
  touchLatamApiUrl,
}: EvidenceModalProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const checkinStatusKey = detail?.checkinStatus as
    | CheckinStatusKey
    | undefined;
  const statusColors = checkinStatusKey
    ? CHECKIN_STATUS_COLORS[checkinStatusKey]
    : undefined;
  const checkinStatusLabel = checkinStatusKey
    ? t(`${BASE}.checkin_status.${checkinStatusKey}.label`)
    : undefined;

  const getFileUrl = useCallback(
    (s3Key: string) => {
      return `${touchLatamApiUrl}/file/get?key=${s3Key}`;
    },
    [touchLatamApiUrl]
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      title={null}
      destroyOnClose
      className="kit-evidence-modal"
      aria-label={detail?.store.name ?? t(`${BASE}.photos.title`)}
    >
      {loading || !detail ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <div className="kit-evidence-modal__content">
          {/* ── Header: tienda + chip turno + captador ────────────── */}
          <div className="kit-evidence-modal__head">
            <div
              className="kit-evidence-modal__store-icon"
              aria-hidden="true"
              style={{
                backgroundColor: token.colorPrimaryBg,
                color: token.colorPrimary,
              }}
            >
              <ShopOutlined />
            </div>
            <div className="kit-evidence-modal__head-info">
              <div className="kit-evidence-modal__store-name-row">
                <Text className="kit-evidence-modal__store-name">
                  {detail.store.name}
                </Text>
                <Tag
                  className="kit-evidence-modal__turn-chip"
                  style={{
                    backgroundColor: token.colorPrimary,
                    borderColor: token.colorPrimary,
                    color: '#fff',
                  }}
                >
                  N° {detail.turn.id}
                </Tag>
              </div>
              <Text className="kit-evidence-modal__store-meta">
                {t(`${BASE}.capturer`)}: {detail.user.name}{' '}
                {detail.user.lastname}
              </Text>
            </div>
          </div>

          {/* ── Banner de tiempo + Turno + Estado ─────────────────── */}
          <div className="kit-evidence-modal__elapsed-banner">
            <ClockCircleOutlined
              className="kit-evidence-modal__elapsed-icon"
              aria-hidden="true"
            />
            <div className="kit-evidence-modal__elapsed-texts">
              <Text className="kit-evidence-modal__elapsed-value">
                {formatElapsed(detail.elapsedSeconds)}
              </Text>
              <Text className="kit-evidence-modal__elapsed-label">
                {t(`${BASE}.elapsed.title`)}
              </Text>
            </div>
            <div className="kit-evidence-modal__elapsed-tags">
              {statusColors && checkinStatusKey && (
                <Tag
                  style={{
                    margin: 0,
                    fontSize: 12,
                    backgroundColor: statusColors.bg,
                    color: statusColors.text,
                    borderColor: statusColors.bg,
                    fontWeight: 600,
                  }}
                >
                  {checkinStatusLabel}
                </Tag>
              )}
            </div>
          </div>

          {/* ── Horarios + Fotos ──────────────────────────────────── */}
          <Row gutter={[20, 20]}>
            <Col xs={24} sm={13}>
              <Text className="kit-evidence-modal__times-title">
                {t(`${BASE}.checkin.section_title`)}
              </Text>
              <div className="kit-evidence-modal__times">
                <TimeBlock
                  icon={
                    <LoginOutlined
                      aria-hidden="true"
                      style={{ fontSize: 13 }}
                    />
                  }
                  label={t(`${BASE}.checkin.title`)}
                  scheduled={dayjs(detail.turn.startDate).format('HH:mm')}
                  actual={
                    detail.checkIn.datetime
                      ? dayjs(detail.checkIn.datetime).format('HH:mm')
                      : null
                  }
                />
                <TimeBlock
                  icon={
                    <LogoutOutlined
                      aria-hidden="true"
                      style={{ fontSize: 13 }}
                    />
                  }
                  label={t(`${BASE}.checkout.title`)}
                  scheduled={dayjs(detail.turn.endDate).format('HH:mm')}
                  actual={
                    detail.checkOut.datetime
                      ? dayjs(detail.checkOut.datetime).format('HH:mm')
                      : null
                  }
                />
              </div>
            </Col>

            <Col xs={24} sm={11}>
              <Text className="kit-evidence-modal__photos-title">
                {t(`${BASE}.photos.title`)}
              </Text>
              <div className="kit-evidence-modal__photos">
                <Image.PreviewGroup>
                  <PhotoCard
                    label={t(`${BASE}.photos.checkin`)}
                    imageUrl={
                      detail.checkIn.photoS3Key
                        ? getFileUrl(detail.checkIn.photoS3Key)
                        : undefined
                    }
                  />
                  <PhotoCard
                    label={t(`${BASE}.photos.checkout`)}
                    imageUrl={
                      detail.checkOut.photoS3Key
                        ? getFileUrl(detail.checkOut.photoS3Key)
                        : undefined
                    }
                  />
                </Image.PreviewGroup>
              </div>
            </Col>
          </Row>

          {/* ── Geolocalización avanzada ──────────────────────────── */}
          <div className="kit-evidence-modal__geo">
            <Text className="kit-evidence-modal__geo-title">
              {t(`${BASE}.geolocation.title`)}
            </Text>
            <EvidenceMap detail={detail} />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default EvidenceModal;
